import styled from "@emotion/styled";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useContext} from "react";
import {ManifestStop, NestedJobStop} from "../../../generated/graphql";
import {ServiceBadge} from "../../common/ServiceBadge";
import {isBothStop, isCompleted} from "../../../utils/stops/StopUtils";
import {getStopPrepositionForType, getStopTimeByStopType} from "../../../utils/stops/StopTimeUtils";
import {EntityVisibilityLevel} from "../MapVisibilityContext";
import {PreferenceContext, extractSimplePref} from "../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../constants/constants";
import {useAppContext} from "../../../ApplicationContext";
import {UnknownString, getDateTimeAtSpecificTimeZone, getLocalDateTime} from "../../../utils/DateTimeUtils";
import {TManifestStopEnhanced} from "../../common/types";

const STOP_TYPE_COLORS: Record<string, string> = {
  P: "#406AFF", // Pickup — blue
  D: "#32A467", // Delivery — green
  B: "#32A467" // Both — green
};

const getStopFooterColor = (
  stopType: string | null | undefined,
  jobStopStatus: string | null | undefined,
  isRoutePlanning?: boolean
): string => {
  if (jobStopStatus === "C") return "#25A567";
  if (isRoutePlanning) return STOP_TYPE_COLORS[stopType ?? ""] ?? "#777777";
  return "#777777";
};

interface StopInfoProps {
  info: StopInfoMetadata;
  stop: NestedJobStop | TManifestStopEnhanced;
  color: string;
}

export interface StopInfoMetadata {
  key: string;
  jobNumber: string;
  customer: string;
  service: string;
  visibilityLevel: EntityVisibilityLevel;
  routeColor?: string;
  isFirstStop: boolean;
  isRoutePlanning?: boolean;
}

const StopInfoContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  width: 290px;
  row-gap: 10px;
  padding-bottom: 8px;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  padding-left: 3px;
`;

const Footer = styled.div<{footerColor: string}>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding-top: 5px;
  padding-left: 3px;
  font-weight: 700;
  color: ${(props) => props.footerColor};
`;

const StopTime = styled.div<{stopStatus: string}>`
  color: ${(props) => (props.stopStatus === "C" ? "#777777" : "#000000")};
`;

const StopTitle = styled.div`
  font-style: normal;
  font-weight: 700;
  max-width: 180px;
  padding-left: 8px;
`;

const StopSequenceCounter = styled.span`
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffff;
`;

const Address = styled.div`
  display: flex;
  flex-direction: column;
  color: #777777;
  max-width: 200px;
`;

const JobNumber = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: right;
  align-items: flex-end;
  padding-right: 10px;
  color: #777777;
  font-weight: 500;
  max-width: 200px;
`;

const ServiceContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: right;
  align-items: flex-end;
`;

const renderStopTitle = (stop: NestedJobStop | ManifestStop, isRoutePlanning?: boolean) => {
  switch (stop.stopType) {
    case "P":
      if (isCompleted(stop)) {
        return <div>PICKED UP</div>;
      } else {
        return <div>PICK UP</div>;
      }
    case "D":
      if (isCompleted(stop)) {
        return <div>DELIVERED</div>;
      } else {
        return <div>{isRoutePlanning ? "DELIVERY" : "DROP OFF"}</div>;
      }
    case "B":
      if (isCompleted(stop)) {
        return (
          <div>
            <div>DROPPED OFF</div>
            <div>PICKED UP</div>
          </div>
        );
      } else if (isRoutePlanning) {
        return (
          <div>
            <div style={{color: STOP_TYPE_COLORS.D}}>DELIVERY</div>
            <div style={{color: STOP_TYPE_COLORS.P}}>PICK UP</div>
          </div>
        );
      } else {
        return (
          <div>
            <div>DROP OFF</div>
            <div>PICK UP</div>
          </div>
        );
      }
  }
};

const getStopInfoDateTimeFormat = (preposition: string | null, isTimeFormat24hr: boolean) => {
  const timeFormat = ["MMM d"];
  if (preposition) timeFormat.push(`'${preposition}'`);
  if (isTimeFormat24hr) {
    timeFormat.push("HH:mm");
  } else {
    timeFormat.push("h:mm aaa");
  }
  timeFormat.push("zzz");

  return timeFormat.join(" ");
};

const renderStopTime = (
  stop: NestedJobStop | ManifestStop,
  getDateTimeFnc: ({
    dateStr,
    timeZone,
    isTimeFormat24hr,
    timeFormat
  }: {
    dateStr: UnknownString;
    timeZone: UnknownString;
    isTimeFormat24hr: boolean;
    timeFormat?: string;
  }) => string | null,
  isTimeFormat24hr: boolean
) => {
  if (!stop.timeZone) return null;

  if (isBothStop(stop)) {
    const pickUpTime = getStopTimeByStopType(stop, "B");
    const pickupPrep = getStopPrepositionForType(stop, "B");
    const pickupTimeFormat = getStopInfoDateTimeFormat(pickupPrep, isTimeFormat24hr);

    const deliveryTime = getStopTimeByStopType(stop, "D");
    const deliveryPrep = getStopPrepositionForType(stop, "D");
    const deliveryTimeFormat = getStopInfoDateTimeFormat(deliveryPrep, isTimeFormat24hr);

    const pickupDateTimeString = getDateTimeFnc({
      dateStr: pickUpTime,
      isTimeFormat24hr,
      timeZone: stop.timeZone,
      timeFormat: pickupTimeFormat
    });
    const deliveryDateTimeString = getDateTimeFnc({
      dateStr: deliveryTime,
      isTimeFormat24hr,
      timeZone: stop.timeZone,
      timeFormat: deliveryTimeFormat
    });

    return (
      <div>
        <StopTime stopStatus={stop.jobStopStatus as string}>{deliveryDateTimeString}</StopTime>
        <StopTime stopStatus={stop.jobStopStatus as string}>{pickupDateTimeString}</StopTime>
      </div>
    );
  } else {
    const time = getStopTimeByStopType(stop, stop.stopType);
    const prep = getStopPrepositionForType(stop, stop.stopType);
    const timeFormat = getStopInfoDateTimeFormat(prep, isTimeFormat24hr);

    const dateTimeString = getDateTimeFnc({
      dateStr: time,
      isTimeFormat24hr,
      timeZone: stop.timeZone,
      timeFormat
    });

    return <StopTime stopStatus={stop.jobStopStatus as string}>{dateTimeString}</StopTime>;
  }
};

export const isManifestStop = (s: NestedJobStop | TManifestStopEnhanced): s is TManifestStopEnhanced => {
  return (s as TManifestStopEnhanced).__typename === "ManifestStop";
};

export const getStopSequence = (stop: NestedJobStop | TManifestStopEnhanced) => {
  return isManifestStop(stop) && stop.optimizedSequence
    ? stop.optimizedSequence
    : stop.manifestSequence ?? stop.sequence;
};

const StopInfo = ({info, stop, color}: StopInfoProps) => {
  const {tenantPreferences} = useContext(PreferenceContext);
  const {isTimeFormat24hr} = useAppContext().appState;
  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;
  const getStopDateTimeFnc = timezoneOfJobStop ? getDateTimeAtSpecificTimeZone : getLocalDateTime;

  const stopSequence = getStopSequence(stop);
  const footerColor = getStopFooterColor(stop.stopType, stop.jobStopStatus, info.isRoutePlanning);

  return (
    <StopInfoContainer data-testid={`stop-info-${stop.jobStopId}`}>
      <Title>
        <span className="fa-stack">
          <FontAwesomeIcon size={"sm"} className="fa-stack-2x" icon={solid("circle")} color={color} />
          <StopSequenceCounter className="fa-stack-1x">{stopSequence}</StopSequenceCounter>
        </span>
        <StopTitle>{stop.name}</StopTitle>
        <ServiceContainer>
          <ServiceBadge service={info.service} />
        </ServiceContainer>
      </Title>
      <Info>
        <Address>
          <div>{info.customer}</div>
          <div>{stop.address}</div>
          <div>
            {stop.city}, {stop.state}, {stop.dispatchZone}
          </div>
        </Address>
        <JobNumber>Job {info.jobNumber}</JobNumber>
      </Info>
      <Footer footerColor={footerColor}>
        <div>{renderStopTitle(stop, info.isRoutePlanning)}</div>
        <div style={{paddingLeft: "8px"}}>{renderStopTime(stop, getStopDateTimeFnc, isTimeFormat24hr)}</div>
      </Footer>
    </StopInfoContainer>
  );
};

export default StopInfo;
